/**
 * DashboardNOC — Painel NOC v3 (UI Rework Global).
 *
 * Reorganiza o dashboard nas 7 rows do briefing v3:
 *   ROW 1 — cards de status (online, offline, OTA, watchdog,
 *           crítico, instalações)
 *   ROW 2 — mini-mapa da frota
 *   ROW 3 — timeline OTA
 *   ROW 4 — últimos alertas
 *   ROW 5 — últimas OS
 *   ROW 6 — atividade de automação
 *   ROW 7 — devices problemáticos
 *
 * NÃO altera o backend nem as APIs — consome só endpoints que já
 * existem. Cada bloco é best-effort: a falha de um fetch não
 * derruba o painel inteiro.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getDashboardSummary,
  getFleetMapStats,
  getOtaHealth,
  healthRankings,
  healthSummary,
  listAlerts,
  listAutomationRules,
  listInstallations,
} from "../lib/api.js";
import { useDashboardWs } from "../hooks/useDashboardWs.js";

/* Atalhos de navegação do painel — só navegam (nenhum dispara comando/OTA).
   Cada target é uma view válida do App. */
const QUICK_ACTIONS = [
  { target: "devices", icon: "🖥️", label: "Dispositivos" },
  { target: "fleet", icon: "🗺️", label: "Mapa da frota" },
  { target: "provisioning", icon: "🧩", label: "Provisionamento" },
  { target: "inventory", icon: "📦", label: "Estoque" },
  { target: "templates", icon: "🗂️", label: "Templates" },
  { target: "accounts", icon: "👤", label: "Contas" },
  { target: "health", icon: "❤️", label: "Saúde" },
];

/* ════════ Componente principal ════════ */
export default function DashboardNOC({ onNavigate }) {
  const [summary, setSummary] = useState(null);
  const [otaHealth, setOtaHealth] = useState(null);
  const [mapStats, setMapStats] = useState(null);
  const [healthSum, setHealthSum] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [installs, setInstalls] = useState([]);
  const [problemDevices, setProblemDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const debounceRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setError(null);
    // Cada bloco é best-effort — Promise.allSettled para que a
    // falha de um endpoint não derrube o painel.
    // Etapa 4A — fontes corrigidas: healthSummary (Watchdog/Crítico/
    // disponibilidade segmentada), healthRankings (problemáticos),
    // listInstallations (OS reais — /work-orders é 404 nesta build),
    // getOtaHealth com a ASSINATURA correta ({days}) — antes recebia
    // um número e a janela caía no default.
    const results = await Promise.allSettled([
      getDashboardSummary(),
      getOtaHealth({ days: 14 }),
      getFleetMapStats(),
      healthSummary(),
      healthRankings({ limit: 8 }),
      listAlerts({ status: "open", limit: 6 }),
      listAutomationRules(),
      listInstallations({}),
    ]);
    const [sum, ota, map, hsum, rank, alr, rul, ins] = results;
    if (sum.status === "fulfilled") setSummary(sum.value);
    else setError("Falha ao carregar o resumo do painel.");
    if (ota.status === "fulfilled") setOtaHealth(ota.value);
    if (map.status === "fulfilled") setMapStats(map.value);
    if (hsum.status === "fulfilled") setHealthSum(hsum.value);
    if (rank.status === "fulfilled") {
      // top_worst já vem ordenado do pior pro melhor; só descartamos
      // quem está saudável (score 100) p/ não poluir a tabela.
      const worst = rank.value?.top_worst || [];
      setProblemDevices(
        worst.filter((d) => (d.health_status || "") !== "healthy")
      );
    }
    if (alr.status === "fulfilled") {
      setAlerts(alr.value?.alerts || alr.value?.items || []);
    }
    if (rul.status === "fulfilled") {
      setRules(rul.value?.rules || rul.value?.items || []);
    }
    if (ins.status === "fulfilled") {
      const sess = ins.value?.sessions || ins.value?.items || [];
      // O painel mostra só instalações com substância. Rascunhos abandonados
      // (status=draft, sem device) são ruído de wizard/teste — poluem a KPI
      // "Instalações" e a lista de OS com sessões que nunca viraram nada.
      setInstalls(sess.filter((s) => String(s.status || "") !== "draft"));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Heartbeats em tempo real — debounce de 1.5s evita burst.
  useDashboardWs(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getDashboardSummary().then(setSummary).catch(() => {});
    }, 1500);
  });

  if (loading && !summary) {
    return (
      <div className="text-sm font-mono text-[12px] text-noc-faint py-8">
        Carregando o painel NOC…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="font-mono text-[10px] tracking-[0.2em]
                          text-noc-accent mb-1">
            {"// VISÃO OPERACIONAL"}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            Painel NOC
          </h1>
        </div>
        <button
          onClick={fetchAll}
          className="btn-primary"
        >
          ↻ Atualizar
        </button>
      </div>

      {error && (
        <div className="text-sm text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded-lg p-3">
          {error}
        </div>
      )}

      {/* ROW 1 — KPI strip (mockup: número grande + subtítulo operacional).
          Etapa 4A — métricas corrigidas: Watchdog/Crítico vêm do
          healthSummary (antes mostravam contadores de OTA trocados). */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3
                        lg:grid-cols-4 xl:grid-cols-7 gap-3">
          <StatCard
            label="Online" value={summary.devices_online}
            sub={`de ${summary.devices_total ?? "—"} dispositivos`}
            tone="ok"
            onClick={() => onNavigate?.("devices")}
          />
          <StatCard
            label="Offline" value={summary.devices_offline}
            sub="sem contato"
            tone={summary.devices_offline > 0 ? "err" : "muted"}
            onClick={() => onNavigate?.("devices")}
          />
          <StatCard
            label="OTA ativo" value={summary.ota_pending_now}
            sub={`${summary.rollouts_active ?? 0} rollout(s) ativo(s)`}
            tone={
              summary.ota_pending_now > 0
                || summary.rollouts_active > 0
                ? "warn" : "muted"
            }
            onClick={() => onNavigate?.("ota")}
          />
          <StatCard
            label="Watchdog"
            value={healthSum ? healthSum.watchdog_high : "—"}
            sub="WD alto nas 24h"
            tone={
              healthSum?.watchdog_high > 0 ? "warn" : "muted"
            }
            onClick={() => onNavigate?.("health")}
          />
          <StatCard
            label="Crítico"
            value={healthSum ? healthSum.critical : "—"}
            sub="saúde crítica"
            tone={healthSum?.critical > 0 ? "err" : "muted"}
            onClick={() => onNavigate?.("health")}
          />
          <StatCard
            label="Instalações" value={installs.length}
            sub="instalações recentes"
            tone={installs.length > 0 ? "info" : "muted"}
            onClick={() => onNavigate?.("installations")}
          />
          <StatCard
            label="Protegidos"
            value={summary.devices_protected ?? "—"}
            sub="proteção efetiva (ENV + DB)"
            tone={summary.devices_protected > 0 ? "info" : "muted"}
            onClick={() => onNavigate?.("fleet")}
          />
        </div>
      )}

      {/* Ações rápidas — atalhos de NAVEGAÇÃO (nunca disparam comando). */}
      <div className="flex flex-wrap gap-2">
        {QUICK_ACTIONS.map((a) => (
          <button
            key={a.target}
            type="button"
            onClick={() => onNavigate?.(a.target)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg
                       border border-noc-divider text-noc-muted
                       hover:border-noc-accent/50 hover:text-noc-text
                       transition-colors"
          >
            <span aria-hidden>{a.icon}</span>
            {a.label}
          </button>
        ))}
      </div>

      {/* Grid principal — operação à esquerda (2/3), feeds à direita (1/3) */}
      <div className="grid lg:grid-cols-3 gap-4 items-start">
        {/* Coluna esquerda */}
        <div className="lg:col-span-2 space-y-4 min-w-0">

          {/* Disponibilidade da frota — barra segmentada (mockup) */}
          <Row
            title="Disponibilidade da frota"
            action={{ label: "Abrir mapa",
                      onClick: () => onNavigate?.("fleet") }}
          >
            <FleetAvailability
              health={healthSum} stats={mapStats} summary={summary}
            />
          </Row>

          {/* Timeline OTA */}
          <Row
            title="Timeline OTA — 14 dias"
            action={{ label: "OTA Manager",
                      onClick: () => onNavigate?.("ota") }}
          >
            {otaHealth?.days?.length ? (
              <OtaSpark days={otaHealth.days} />
            ) : (
              <Empty>Sem eventos OTA recentes.</Empty>
            )}
          </Row>

          {/* Devices problemáticos — tabela rica (healthRankings.top_worst) */}
          <Row
            title="Dispositivos problemáticos"
            action={{ label: "Ver todos",
                      onClick: () => onNavigate?.("health") }}
          >
            {problemDevices.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="font-mono text-[10px] uppercase
                                   tracking-[0.14em] text-noc-faint
                                   text-left border-b border-noc-divider">
                      <th className="py-1.5 pr-2">Dispositivo</th>
                      <th className="py-1.5 pr-2">Saúde</th>
                      <th className="py-1.5 pr-2">Reboots 24h</th>
                      <th className="py-1.5 pr-2">Último contato</th>
                      <th className="py-1.5 text-right">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {problemDevices.slice(0, 8).map((d, i) => (
                      <tr key={d.device_id || i}
                          className="border-b border-noc-divider/60
                                     last:border-0">
                        <td className="py-2 pr-2 font-mono">
                          <span className="text-noc-text">{d.device_id}</span>
                          {d.customer_name && (
                            <span className="text-noc-faint ml-2 text-[10px]">
                              #{d.customer_name}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-2">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={
                              d.health_status === "critical"
                                ? "led-err"
                                : d.health_status === "warning"
                                  ? "led-warn" : "led-ok"
                            } />
                            <span className="font-mono">
                              {d.health_score ?? "—"}
                            </span>
                          </span>
                        </td>
                        <td className="py-2 pr-2 font-mono">
                          {d.reboot_count_24h ?? "—"}
                        </td>
                        <td className="py-2 pr-2 text-noc-muted">
                          {fmtTime(d.last_online)}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() =>
                              onNavigate?.("detail", d.device_id)}
                            className="font-mono text-[10px] uppercase
                                       tracking-[0.1em] text-noc-accent
                                       hover:underline"
                          >
                            Abrir →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty>Nenhum dispositivo problemático.</Empty>
            )}
          </Row>
        </div>

        {/* Coluna direita — feeds */}
        <div className="space-y-4 min-w-0">

          {/* Últimos alertas */}
          <Row
            title="Últimos alertas"
            action={{ label: "Ver alertas",
                      onClick: () => onNavigate?.("alerts") }}
          >
            {alerts.length ? (
              <div className="space-y-1.5">
                {alerts.slice(0, 6).map((a, i) => (
                  <ListLine
                    key={a.id || i}
                    tone={sevTone(a.severity)}
                    left={a.rule || a.title || "alerta"}
                    mid={a.message}
                    right={fmtTime(a.last_trigger_at || a.created_at)}
                  />
                ))}
              </div>
            ) : (
              <Empty>Nenhum alerta aberto. Tudo tranquilo.</Empty>
            )}
          </Row>

          {/* Ordens de serviço — sessões de instalação REAIS (Field Ops);
              o endpoint /work-orders não existe nesta build (404). */}
          <Row
            title="Ordens de serviço"
            action={{ label: "Field Ops",
                      onClick: () => onNavigate?.("installations") }}
          >
            {installs.length ? (
              <div className="space-y-1.5">
                {installs.slice(0, 5).map((w, i) => (
                  <ListLine
                    key={w.id || i}
                    tone={
                      String(w.status || "").match(/done|finish|conclu/i)
                        ? "ok" : "info"
                    }
                    left={"OS " + String(w.id || "").slice(0, 8)}
                    mid={w.installer || w.qr_serial || "—"}
                    right={String(w.status || "—").toUpperCase()}
                  />
                ))}
              </div>
            ) : (
              <Empty>Nenhuma ordem de serviço.</Empty>
            )}
          </Row>

          {/* Automação */}
          <Row
            title="Automação"
            action={{ label: "Playbooks",
                      onClick: () => onNavigate?.("automation") }}
          >
            {rules.length ? (
              <div className="space-y-1.5">
                {rules.slice(0, 6).map((r, i) => (
                  <div
                    key={r.id || i}
                    className="flex items-center gap-2.5 px-1 py-1.5
                               border-b border-noc-divider last:border-0"
                  >
                    <span className={
                      r.enabled !== false ? "led-ok" : "led-off"
                    } />
                    <span className="text-sm truncate flex-1">
                      {r.name || r.rule || "regra"}
                    </span>
                    <span className={
                      "font-mono text-[10px] "
                      + (r.enabled !== false
                          ? "text-noc-ok" : "text-noc-faint")
                    }>
                      {r.enabled !== false ? "ON" : "OFF"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <Empty>Nenhuma regra de automação configurada.</Empty>
            )}
          </Row>
        </div>
      </div>
    </div>
  );
}

/* ════════ Componentes auxiliares ════════ */
function StatCard({ label, value, sub, tone, onClick }) {
  const tones = {
    ok: "text-noc-ok",
    err: "text-noc-err",
    warn: "text-noc-warn",
    info: "text-noc-accent",
    muted: "text-noc-muted",
  };
  const leds = {
    ok: "led-ok",
    err: "led-err",
    warn: "led-warn",
    info: "led-info",
    muted: "led-off",
  };
  return (
    <button
      onClick={onClick}
      className="stat-tile text-left"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase
                          tracking-[0.16em] text-noc-faint">
          {label}
        </span>
        <span className={leds[tone] || "led-off"} />
      </div>
      <div className={
        "font-mono text-3xl font-semibold tracking-tight mt-2 "
        + (tones[tone] || "text-noc-text")
      }>
        {value ?? "—"}
      </div>
      {/* Etapa 4A — subtítulo operacional do mockup */}
      {sub && (
        <div className="text-[11px] text-noc-muted mt-0.5 truncate">
          {sub}
        </div>
      )}
    </button>
  );
}

function Row({ title, action, children }) {
  return (
    <section className="glass-card-pad">
      <div className="flex items-center gap-3 mb-3">
        <h2 className="section-label flex-1">
          {"// " + title}
        </h2>
        {action && (
          <button
            onClick={action.onClick}
            className="font-mono text-[10px] uppercase tracking-[0.1em]
                       text-noc-accent hover:underline"
          >
            {action.label} →
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function FleetAvailability({ health, stats, summary }) {
  // Etapa 4A — barra SEGMENTADA do mockup (saudável/degradado/crítico/
  // offline) alimentada pelo healthSummary; fallback no summary básico
  // (online/offline) quando o health não respondeu.
  const total = health?.total ?? summary?.devices_total ?? 0;
  const healthy = health?.healthy ?? summary?.devices_online ?? 0;
  const warning = health?.warning ?? 0;
  const critical = health?.critical ?? 0;
  const offline = health?.offline ?? summary?.devices_offline ?? 0;
  const geoLocated = stats?.located ?? stats?.with_location ?? "—";
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);
  const pctOnline = total > 0
    ? Math.round(((health?.online ?? summary?.devices_online ?? 0) / total) * 100)
    : 0;
  const SEGS = [
    { n: healthy, cls: "bg-noc-ok", label: "Saudável" },
    { n: warning, cls: "bg-noc-warn", label: "Degradado" },
    { n: critical, cls: "bg-noc-err", label: "Crítico" },
    { n: offline, cls: "bg-noc-faint/40", label: "Offline" },
  ];
  return (
    <div className="space-y-3">
      {/* Barra segmentada */}
      <div className="h-2.5 rounded-full bg-noc-panel2 overflow-hidden
                      flex">
        {SEGS.map((s) =>
          s.n > 0 ? (
            <div
              key={s.label}
              className={`h-full ${s.cls} transition-all`}
              style={{ width: pct(s.n) + "%" }}
              title={`${s.label}: ${s.n}`}
            />
          ) : null
        )}
      </div>
      {/* Legenda */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono
                      text-[11px]">
        {SEGS.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-sm ${s.cls}`} />
            <span className="text-noc-muted">{s.label}</span>
            <span className="text-noc-text">{s.n}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="text-noc-faint">Total</span>
          <span className="text-noc-text">{total}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-noc-faint">Geolocalizados</span>
          <span className="text-noc-text">{geoLocated}</span>
        </span>
        <span className="ml-auto text-noc-accent">
          {pctOnline}% online
        </span>
      </div>
    </div>
  );
}

function OtaSpark({ days }) {
  // Sparkline simples de barras — sucessos vs falhas por dia.
  // Etapa 4A — eixo com data inicial e marcador HOJE (mockup) +
  // totais do período na lateral.
  const max = Math.max(
    1,
    ...days.map((d) => (d.success || 0) + (d.failed || 0))
  );
  const totalOk = days.reduce((acc, d) => acc + (d.success || 0), 0);
  const totalFail = days.reduce((acc, d) => acc + (d.failed || 0), 0);
  const firstDate = days[0]?.date || "";
  return (
    <div className="space-y-1.5">
      <div className="flex items-end gap-1 h-24">
        {days.map((d, i) => {
          const ok = d.success || 0;
          const fail = d.failed || 0;
          const okH = (ok / max) * 100;
          const failH = (fail / max) * 100;
          return (
            <div
              key={i}
              className="flex-1 flex flex-col justify-end gap-0.5
                         group relative"
              title={`${d.date || ""}: ${ok} ok, ${fail} falhas`}
            >
              {fail > 0 && (
                <div className="bg-noc-err rounded-sm"
                     style={{ height: failH + "%" }} />
              )}
              {ok > 0 && (
                <div className="bg-noc-ok rounded-sm"
                     style={{ height: okH + "%" }} />
              )}
              {ok === 0 && fail === 0 && (
                <div className="bg-noc-panel2 rounded-sm h-1" />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center font-mono text-[10px]
                      text-noc-faint">
        <span>{firstDate}</span>
        <span className="flex-1" />
        <span className="text-noc-muted mr-3">
          <span className="text-noc-ok">{totalOk} ok</span>
          {" · "}
          <span className={totalFail > 0 ? "text-noc-err" : ""}>
            {totalFail} falhas
          </span>
        </span>
        <span className="text-noc-accent tracking-[0.14em]">HOJE</span>
      </div>
    </div>
  );
}

function ListLine({ tone, left, mid, right, mono }) {
  const dots = {
    ok: "bg-noc-ok", err: "bg-noc-err",
    warn: "bg-noc-warn", info: "bg-noc-accent",
  };
  return (
    <div className="flex items-center gap-2.5 text-sm
                    glass-card-pad py-2">
      <span className={
        "h-2 w-2 rounded-full shrink-0 " + (dots[tone] || dots.info)
      } />
      <span className={
        "font-medium shrink-0 " + (mono ? "font-mono text-xs" : "")
      }>
        {left}
      </span>
      <span className="text-noc-muted text-xs flex-1 min-w-0
                       truncate">
        {mid}
      </span>
      <span className="text-[11px] text-noc-muted/70 shrink-0">
        {right}
      </span>
    </div>
  );
}

function Empty({ children }) {
  return (
    <div className="text-sm font-mono text-[12px] text-noc-faint py-2">
      {children}
    </div>
  );
}

/* ════════ helpers ════════ */
function sevTone(s) {
  if (s === "critical") return "err";
  if (s === "warning") return "warn";
  return "info";
}

function fmtTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}
